import type { Filter } from 'components/filters/useStreamFilters'

export type MintableMediaKind = 'image' | 'video' | 'audio' | 'html'
export type MintMediaThreadFlowOptions = { source?: string }

const HTML_MIME_TYPES = new Set(['text/html', 'application/xhtml+xml'])

// Amounts under a cent would round to "$0.00", so they show in sats when the sats are known.
function formatMintAmount(usd: number, sats?: number): string {
    if (usd < 0.01 && Number.isFinite(sats) && (sats as number) >= 0) {
        const wholeSats = Math.round(sats as number)
        return `${wholeSats.toLocaleString('en-US')} ${wholeSats === 1 ? 'sat' : 'sats'}`
    }
    return `$${usd.toFixed(2)}`
}

export function buildMintMediaThreadConfirmation({
    byteSize,
    estimatedOnchainWriteUsd,
    treechatFeeUsd,
    estimatedOnchainWriteSats,
    treechatFeeSats,
}: {
    byteSize: number
    estimatedOnchainWriteUsd: number
    treechatFeeUsd: number
    estimatedOnchainWriteSats?: number
    treechatFeeSats?: number
}) {
    const totalUsd = estimatedOnchainWriteUsd + treechatFeeUsd
    const totalSats =
        estimatedOnchainWriteSats === undefined || treechatFeeSats === undefined
            ? undefined
            : estimatedOnchainWriteSats + treechatFeeSats

    return {
        description: `Inscribe ${byteSize.toLocaleString(
            'en-US',
        )} bytes onchain for ${formatMintAmount(
            estimatedOnchainWriteUsd,
            estimatedOnchainWriteSats,
        )} + ${formatMintAmount(treechatFeeUsd, treechatFeeSats)} to Treechat, and post.`,
        confirmLabel: `MINT + POST ${formatMintAmount(totalUsd, totalSats)}`,
    }
}

export function isOrdinalsStreamFilter(filter?: Filter | null): boolean {
    return Boolean(filter && (filter.ordinals || filter.user_ordinals))
}

export function isOrdinalsMintEnabled(filter?: Filter | null): boolean {
    return isOrdinalsStreamFilter(filter)
}

// Some browsers hand over .html files with an empty MIME type, so the extension
// counts too.
export function mintableMediaKindForFile(
    file: Pick<File, 'type'> & { name?: string },
): MintableMediaKind | null {
    const mimeType = file.type.toLowerCase()
    if (mimeType.startsWith('image/')) return 'image'
    if (mimeType.startsWith('video/')) return 'video'
    if (mimeType.startsWith('audio/')) return 'audio'
    if (isHtmlFile(file)) return 'html'
    return null
}

export function isHtmlFile(file: Pick<File, 'type'> & { name?: string }): boolean {
    const mimeType = file.type.toLowerCase().split(';')[0].trim()
    if (HTML_MIME_TYPES.has(mimeType)) return true
    return !mimeType && /\.html?$/i.test(file.name || '')
}

// Keep the selected file's identity stable when supplying a missing HTML MIME type.
export function normalizeMintMediaFile(file: File): File {
    return mintableMediaKindForFile(file) === 'html' && !file.type
        ? new File([file], file.name, {
              type: 'text/html',
              lastModified: file.lastModified,
          })
        : file
}

export function createMintMediaThreadRunner(
    operation: (options?: MintMediaThreadFlowOptions) => Promise<void>,
) {
    let activeFlow: Promise<void> | null = null

    return (options?: MintMediaThreadFlowOptions): Promise<void> => {
        if (activeFlow) return activeFlow

        const operationPromise = operation(options)
        const guardedFlow = operationPromise.finally(() => {
            if (activeFlow === guardedFlow) activeFlow = null
        })
        activeFlow = guardedFlow
        return guardedFlow
    }
}

// Include bytes as well as metadata: different files can share a name, size and
// modification time. Compute once per selection and reuse for cache operations.
export async function mintSourceRetryKey(userId: string, file: File): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
    const contentHash = Array.from(new Uint8Array(digest), byte =>
        byte.toString(16).padStart(2, '0'),
    ).join('')
    return JSON.stringify([userId, file.name, file.size, file.lastModified, file.type, contentHash])
}

// Remember the hidden source after an ambiguous or failed start, so retries use
// the same post/blob and therefore the same server-side mint intent.
export function createMintSourceRetryCache() {
    const answerIds = new Map<string, string>()

    return {
        find: (key: string) => answerIds.get(key) || null,
        remember: (key: string, answerId: string) => {
            answerIds.set(key, answerId)
        },
        forget: (key: string) => {
            answerIds.delete(key)
        },
    }
}
